import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const FollowerCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 99990;
`;

const BackgroundGlow = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle at center,
    rgba(133, 76, 230, 0.14) 0%,
    rgba(56, 189, 248, 0.06) 40%,
    transparent 70%
  );
  filter: blur(55px);
  will-change: transform, opacity;
  transition: opacity 0.3s ease;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
`;

const CustomCursor = () => {
  const [isTouch, setIsTouch] = useState(false);
  const [glowVisible, setGlowVisible] = useState(false);
  const canvasRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    // Check for touch / mobile or reduced motion
    const checkTouch = () => {
      const hasTouch =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia("(pointer: coarse)").matches;
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      return hasTouch || prefersReducedMotion;
    };

    if (checkTouch()) {
      setIsTouch(true);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    // Precise coordinates
    const mouse = {
      x: -100,
      y: -100,
      targetX: -100,
      targetY: -100,
      vx: 0,
      vy: 0,
      speed: 0,
      angle: 0,
      visible: false,
    };

    // Smooth glow position
    const glowPos = {
      x: -100,
      y: -100,
    };

    // Fluid comet trail nodes
    const TRAIL_COUNT = 9;
    const trail = Array.from({ length: TRAIL_COUNT }, () => ({
      x: -100,
      y: -100,
    }));

    // Interactive state
    let targetRadius = 4.2;
    let currentRadius = 4.2;
    let targetGlowBlur = 10;
    let currentGlowBlur = 10;
    let targetOuterRingRadius = 0;
    let currentOuterRingRadius = 0;
    let targetOuterRingAlpha = 0;
    let currentOuterRingAlpha = 0;

    const interactiveSelector = `
      a, button, input, textarea, select, [role="button"],
      .nav-link, .card, .spotlight-card, [data-interactive="true"]
    `;

    const handleMouseMove = (e) => {
      mouse.targetX = e.clientX;
      mouse.targetY = e.clientY;

      if (!mouse.visible) {
        mouse.x = e.clientX;
        mouse.y = e.clientY;
        glowPos.x = e.clientX;
        glowPos.y = e.clientY;
        for (let i = 0; i < TRAIL_COUNT; i++) {
          trail[i].x = e.clientX;
          trail[i].y = e.clientY;
        }
        mouse.visible = true;
        setGlowVisible(true);
      }

      // Card Spotlight Effect: update CSS variables on cards
      const target = e.target;
      const card = target ? target.closest(".spotlight-card") : null;
      if (card) {
        const rect = card.getBoundingClientRect();
        const cardX = e.clientX - rect.left;
        const cardY = e.clientY - rect.top;
        card.style.setProperty("--mouse-x", `${cardX}px`);
        card.style.setProperty("--mouse-y", `${cardY}px`);
        card.style.setProperty("--spotlight-opacity", "1");
      }

      // Interactive hover check
      const interactiveEl = target ? target.closest(interactiveSelector) : null;
      if (interactiveEl) {
        targetRadius = 6;
        targetGlowBlur = 22;
        targetOuterRingRadius = 24;
        targetOuterRingAlpha = 0.55;
      } else {
        targetRadius = 4.2;
        targetGlowBlur = 10;
        targetOuterRingRadius = 0;
        targetOuterRingAlpha = 0;
      }
    };

    const handleMouseLeave = () => {
      mouse.visible = false;
      setGlowVisible(false);
    };

    const handleMouseEnter = () => {
      mouse.visible = true;
      setGlowVisible(true);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("mouseenter", handleMouseEnter);

    let lastTime = performance.now();

    // Main render loop
    const render = (time) => {
      const dt = Math.min((time - lastTime) / 1000, 0.1);
      lastTime = time;

      ctx.clearRect(0, 0, width, height);

      if (mouse.visible) {
        // High-precision tracking for zero lag
        const prevX = mouse.x;
        const prevY = mouse.y;

        mouse.x = mouse.targetX;
        mouse.y = mouse.targetY;

        // Calculate velocity and motion dynamics
        mouse.vx = mouse.x - prevX;
        mouse.vy = mouse.y - prevY;
        mouse.speed = Math.sqrt(mouse.vx * mouse.vx + mouse.vy * mouse.vy);
        if (mouse.speed > 0.1) {
          mouse.angle = Math.atan2(mouse.vy, mouse.vx);
        }

        // Responsive background glow follow
        const glowLerp = 1 - Math.pow(0.001, dt * 14);
        glowPos.x += (mouse.targetX - glowPos.x) * glowLerp;
        glowPos.y += (mouse.targetY - glowPos.y) * glowLerp;

        if (glowRef.current) {
          glowRef.current.style.transform = `translate(${glowPos.x}px, ${glowPos.y}px) translate(-50%, -50%)`;
        }

        // Fast & fluid trail nodes
        trail[0].x += (mouse.x - trail[0].x) * 0.8;
        trail[0].y += (mouse.y - trail[0].y) * 0.8;
        for (let i = 1; i < TRAIL_COUNT; i++) {
          trail[i].x += (trail[i - 1].x - trail[i].x) * 0.68;
          trail[i].y += (trail[i - 1].y - trail[i].y) * 0.68;
        }

        // Interpolate hover values quickly
        currentRadius += (targetRadius - currentRadius) * 0.28;
        currentGlowBlur += (targetGlowBlur - currentGlowBlur) * 0.28;
        currentOuterRingRadius += (targetOuterRingRadius - currentOuterRingRadius) * 0.28;
        currentOuterRingAlpha += (targetOuterRingAlpha - currentOuterRingAlpha) * 0.28;

        // 1. Draw smooth fluid motion trail behind the dot
        if (mouse.speed > 0.05 || currentOuterRingAlpha > 0.05) {
          ctx.beginPath();
          ctx.moveTo(trail[0].x, trail[0].y);
          for (let i = 1; i < TRAIL_COUNT; i++) {
            ctx.lineTo(trail[i].x, trail[i].y);
          }
          ctx.strokeStyle = `rgba(133, 76, 230, ${Math.min(0.35, 0.12 + mouse.speed * 0.02)})`;
          ctx.lineWidth = Math.max(1.5, Math.min(4, mouse.speed * 0.4));
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.shadowBlur = 8;
          ctx.shadowColor = "rgba(133, 76, 230, 0.7)";
          ctx.stroke();

          // Elegant particle nodes along trail
          for (let i = TRAIL_COUNT - 1; i >= 1; i--) {
            const ratio = (TRAIL_COUNT - i) / TRAIL_COUNT;
            const nodeRadius = Math.max(1, (currentRadius * 0.6) * ratio);
            const alpha = 0.22 * ratio;

            ctx.beginPath();
            ctx.arc(trail[i].x, trail[i].y, nodeRadius, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.shadowBlur = 6;
            ctx.shadowColor = "rgba(56, 189, 248, 0.4)";
            ctx.fill();
          }
        }

        // 2. Interactive Outer Expanding Halo
        if (currentOuterRingAlpha > 0.02) {
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, currentOuterRingRadius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255, 255, 255, ${currentOuterRingAlpha})`;
          ctx.lineWidth = 1.3;
          ctx.shadowBlur = 14;
          ctx.shadowColor = "rgba(133, 76, 230, 0.8)";
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, currentOuterRingRadius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(133, 76, 230, ${currentOuterRingAlpha * 0.12})`;
          ctx.fill();
        }

        // 3. Main Glowing White Dot with speed stretch / squash dynamic
        const stretch = Math.min(mouse.speed * 0.08, 0.5);
        const rx = currentRadius * (1 + stretch);
        const ry = currentRadius * (1 - stretch * 0.4);

        ctx.save();
        ctx.translate(mouse.x, mouse.y);
        ctx.rotate(mouse.angle);
        ctx.beginPath();
        ctx.ellipse(0, 0, Math.max(1, rx), Math.max(1, ry), 0, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.shadowBlur = currentGlowBlur + Math.min(mouse.speed * 0.5, 12);
        ctx.shadowColor = "rgba(255, 255, 255, 0.95)";
        ctx.fill();
        ctx.restore();
      }

      animationFrameId = requestAnimationFrame(render);
    };

    animationFrameId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, []);

  if (isTouch) {
    return null;
  }

  return (
    <>
      <BackgroundGlow ref={glowRef} $visible={glowVisible} />
      <FollowerCanvas ref={canvasRef} />
    </>
  );
};

export default CustomCursor;
